import { Scale, TrendingUp } from "lucide-react";
import { AGENT_ANALYTICS_SHELL_STYLE } from "@/lib/agentVisualTokens";
import { formatCurrency } from "@/lib/portalHelpers";

const metricRows = (forecast) => [
  { label: "Rental yield", value: forecast?.estimated_rental_yield_pct ? `${forecast.estimated_rental_yield_pct.toFixed(2)}%` : "—" },
  { label: "Base case · 2031", value: forecast ? `+${forecast.five_year_appreciation_pct}%` : "—" },
  { label: "Conservative · 2031", value: forecast ? `+${forecast.conservative_five_year_appreciation_pct}%` : "—" },
  { label: "Best case · 2031", value: forecast ? `+${forecast.best_case_five_year_appreciation_pct}%` : "—" },
];

const ForecastCard = ({ isAgentTheme = false, listing, forecast, tone = "default" }) => {
  const cardClass = isAgentTheme
    ? `editorial-dark-soft rounded-[24px] border p-5 ${tone === "current" ? "border-[#D4AF37]/30 bg-[#121212]" : "border-white/10 bg-[#121212]"}`
    : `rounded-[24px] border p-5 ${tone === "current" ? "border-[#D4AF37]/28 bg-[#fff7e9]" : "border-[#001935]/10 bg-white"}`;
  const eyebrowClass = isAgentTheme ? "text-[#D4AF37]" : "text-[#8b6d16]";
  const titleClass = isAgentTheme ? "text-[#F9F8F6]" : "text-[#001935]";
  const metaClass = isAgentTheme ? "text-[#E7E0D4]" : "text-[#5D584F]";
  const valueClass = isAgentTheme ? "text-[#F9F8F6]" : "text-[#001935]";
  return (
    <div className={cardClass} data-testid={`forecast-compare-card-${listing.id}`}>
      <p className={`text-xs font-bold uppercase tracking-[0.18em] ${eyebrowClass}`}>{tone === "current" ? "Current listing" : "Comparison listing"}</p>
      <h3 className={`mt-3 font-[Cormorant_Garamond,serif] text-3xl font-bold ${titleClass}`} data-testid={`forecast-compare-card-title-${listing.id}`}>{listing.title}</h3>
      <p className={`mt-2 text-sm ${metaClass}`} data-testid={`forecast-compare-card-meta-${listing.id}`}>{listing.district} · {listing.property_type}</p>
      <p className={`mt-3 text-2xl font-semibold ${valueClass}`} data-testid={`forecast-compare-card-price-${listing.id}`}>{formatCurrency(listing.price || 0)}</p>
      <div className="mt-4 space-y-3">
        {metricRows(forecast).map((row) => (
          <div className="flex items-center justify-between gap-3 text-sm" key={`${listing.id}-${row.label}`}>
            <span className={metaClass}>{row.label}</span>
            <span className={`font-semibold ${valueClass}`} data-testid={`forecast-compare-card-${listing.id}-${row.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const ForecastComparisonPanel = ({ comparedForecasts, currentForecast, currentListing, isAgentTheme = false, loading, onToggle, selectedIds, selectableListings }) => {
  if (!currentListing || !currentForecast) return null;

  const sectionClass = isAgentTheme
    ? "agent-paper-section editorial-dark-shell mt-8 rounded-[34px] p-6 shadow-[0_24px_80px_rgba(0,0,0,0.28)] md:p-8"
    : "mt-8 rounded-[34px] border border-[#D4AF37]/20 bg-[linear-gradient(135deg,#fff9ef_0%,#f7efe2_56%,#efe2c8_100%)] p-6 shadow-[0_24px_80px_rgba(90,75,53,0.08)] md:p-8";
  const headingClass = isAgentTheme ? "text-[#F9F8F6]" : "text-[#001935]";
  const copyClass = isAgentTheme ? "text-[#DDD6C9]" : "text-[#42566f]";
  const pillClass = isAgentTheme
    ? "inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/25 bg-[#121212] px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#D4AF37]"
    : "inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/25 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#8b6d16]";
  const eyebrow = isAgentTheme ? "Compare nearby homes" : "Compare Forecasts";
  const heading = isAgentTheme ? "See how this home stacks up against nearby options in the same district." : "Side-by-side Alpha Formula projections for this district.";
  const summary = isAgentTheme ? "Select up to two nearby homes to compare projected value ranges, rental yield, and current pricing at a glance." : "Select up to two nearby listings to compare the 5-year base case, confidence bands, and current rental yield side by side.";
  const pillLabel = isAgentTheme ? "Nearby home view" : "Alpha Formula view";

  return (
    <section className={sectionClass} data-testid="listing-detail-forecast-compare-section" style={isAgentTheme ? AGENT_ANALYTICS_SHELL_STYLE : undefined}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#D4AF37]">{eyebrow}</p>
          <h2 className={`mt-3 font-[Cormorant_Garamond,serif] text-4xl font-bold md:text-5xl ${headingClass}`} data-testid="listing-detail-forecast-compare-heading">{heading}</h2>
          <p className={`mt-4 text-sm leading-8 ${copyClass}`}>{summary}</p>
        </div>
        <div className={pillClass} data-testid="listing-detail-forecast-compare-pill">
          <Scale className="h-4 w-4" /> {pillLabel}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-3" data-testid="listing-detail-forecast-compare-selector-list">
        {selectableListings.map((item) => {
          const active = selectedIds.includes(item.id);
          return (
            <button
              className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${active ? (isAgentTheme ? "border-[#D4AF37] bg-[#2A2110] text-[#D4AF37]" : "border-[#D4AF37] bg-[#fff4dd] text-[#8b6d16]") : (isAgentTheme ? "border-white/12 bg-[#121212] text-[#DDD6C9] hover:border-[#D4AF37]/45 hover:text-[#D4AF37]" : "border-[#001935]/10 bg-white text-[#42566f] hover:border-[#D4AF37]/45 hover:text-[#8b6d16]")}`}
              data-testid={`listing-detail-forecast-compare-toggle-${item.id}`}
              key={item.id}
              onClick={() => onToggle(item.id)}
              type="button"
            >
              {active ? "Selected" : "Compare"} · {item.title}
            </button>
          );
        })}
      </div>

      <div className="mt-6 grid gap-4 xl:grid-cols-3">
        <ForecastCard forecast={currentForecast} isAgentTheme={isAgentTheme} listing={currentListing} tone="current" />
        {selectedIds.map((listingId) => {
          const selectedListing = selectableListings.find((item) => item.id === listingId);
          const selectedForecast = comparedForecasts[listingId];
          if (!selectedListing) return null;
          return <ForecastCard forecast={selectedForecast} isAgentTheme={isAgentTheme} key={listingId} listing={selectedListing} />;
        })}
      </div>

      {loading ? (
        <div className={`mt-5 rounded-[24px] border px-5 py-4 text-sm leading-7 ${isAgentTheme ? "border-[#D4AF37]/20 bg-[#121212] text-[#DDD6C9]" : "border-[#D4AF37]/20 bg-white/75 text-[#6b6252]"}`} data-testid="listing-detail-forecast-compare-loading">
          <TrendingUp className="mr-2 inline-flex h-4 w-4" /> Preparing the comparison forecasts...
        </div>
      ) : null}
    </section>
  );
};